import { useEffect, useState } from "react";
import { api, formatTZS, formatDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Users, Calendar, Phone, Mail, Send, MessageSquare } from "lucide-react";

const badge = {
  pending: "bg-amber-100 text-amber-700",
  quoted: "bg-blue-100 text-blue-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700",
};

export default function ServiceQuotesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(null);
  const [replyForm, setReplyForm] = useState({ quoted_price: "", quote_notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/service-quotes"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openReply = (q) => {
    setReplying(q);
    setReplyForm({ quoted_price: q.quoted_price || "", quote_notes: q.quote_notes || "" });
  };

  const submitReply = async () => {
    if (!replyForm.quoted_price || Number(replyForm.quoted_price) <= 0) return toast.error("Enter a price");
    setSubmitting(true);
    try {
      await api.post(`/admin/service-quotes/${replying.id}/reply`, {
        quoted_price: Number(replyForm.quoted_price),
        quote_notes: replyForm.quote_notes,
      });
      toast.success("Quote sent to guest");
      setReplying(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand)]" /></div>;

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div>
        <h1 className="font-heading text-3xl text-[color:var(--deep)]">Custom Quote Requests</h1>
        <p className="text-sm text-[color:var(--muted-text)] mt-1">Bespoke requests from guests — reply with a live price and any conditions. They can accept or decline online.</p>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border text-center text-sm text-[color:var(--muted-text)]" data-testid="empty-quotes">No custom quote requests yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((q) => (
            <div key={q.id} className="bg-white rounded-2xl border border-[color:var(--brand)]/10 p-5" data-testid={`quote-${q.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-[color:var(--muted-text)]">{q.reference}</p>
                  <p className="font-heading text-lg text-[color:var(--deep)] mt-0.5">{q.service_title}</p>
                </div>
                <Badge className={`${badge[q.status] || ""} border-0 capitalize`}>{q.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 text-sm font-ui">
                <div className="flex items-center gap-2 text-[color:var(--deep)]"><Users className="w-3.5 h-3.5 text-[color:var(--brand)]" /> {q.guest_name} · {q.guests} pax</div>
                <div className="flex items-center gap-2 text-[color:var(--deep)]"><Calendar className="w-3.5 h-3.5 text-[color:var(--brand)]" /> {formatDate(q.event_date)}</div>
                <div className="flex items-center gap-2 text-[color:var(--muted-text)] text-xs"><Phone className="w-3 h-3" /> {q.guest_phone}</div>
                <div className="flex items-center gap-2 text-[color:var(--muted-text)] text-xs truncate"><Mail className="w-3 h-3" /> {q.guest_email}</div>
              </div>
              {q.menu_notes && <p className="mt-3 text-xs text-[color:var(--deep)] bg-[color:var(--sand)] rounded-lg p-3"><b>Menu:</b> {q.menu_notes}</p>}
              {q.requirements && <p className="mt-2 text-xs text-[color:var(--deep)] bg-[color:var(--sand)] rounded-lg p-3"><b>Requirements:</b> {q.requirements}</p>}
              {q.status !== "pending" && q.quoted_price != null && (
                <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                  <p className="font-ui text-xs uppercase tracking-wider text-blue-700">Your quote</p>
                  <p className="font-heading text-lg text-[color:var(--deep)] mt-0.5">{formatTZS(q.quoted_price)}</p>
                  {q.quote_notes && <p className="text-xs text-[color:var(--deep)] mt-1">{q.quote_notes}</p>}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" onClick={() => openReply(q)} data-testid={`reply-quote-${q.id}`} className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
                  <Send className="w-3.5 h-3.5 mr-1" /> {q.status === "pending" ? "Send price" : "Update price"}
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a href={`https://wa.me/${(q.guest_phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Habari ${q.guest_name}, kuhusu ombi lako la ${q.service_title} (${q.reference})...`)}`} target="_blank" rel="noreferrer" data-testid={`wa-quote-${q.id}`}>
                    <MessageSquare className="w-3.5 h-3.5 mr-1" /> WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!replying} onOpenChange={(v) => !v && setReplying(null)}>
        <DialogContent data-testid="quote-reply-dialog">
          <DialogHeader>
            <DialogTitle>Reply to {replying?.reference}</DialogTitle>
            <DialogDescription>{replying?.service_title} for {replying?.guests} guests on {replying?.event_date && formatDate(replying.event_date)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Quoted price (TZS)</Label><Input type="number" min="0" value={replyForm.quoted_price} onChange={(e) => setReplyForm({ ...replyForm, quoted_price: e.target.value })} data-testid="reply-price" className="mt-1" /></div>
            <div><Label>Notes / conditions</Label><Textarea rows={4} value={replyForm.quote_notes} onChange={(e) => setReplyForm({ ...replyForm, quote_notes: e.target.value })} placeholder="Includes buffet lunch, 2 coffee breaks, projector. Deposit 50% required 3 days prior." data-testid="reply-notes" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplying(null)}>Cancel</Button>
            <Button onClick={submitReply} disabled={submitting} data-testid="reply-submit" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
